import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";
import { devWarn } from "@/lib/logger";

function geocodeButtonLabel({ geocoding, homeGeo }) {
  if (geocoding) return "Geocoding…";
  return homeGeo ? "Re-geocode" : "Geocode now";
}

export default function StaffProfile() {
  const { user } = useAuth();
  const [p, setP] = useState({
    date_of_birth: "", address: "", city: "", postcode: "",
    experience: "", skills: [], preferred_locations: [], travel_distance: 25,
    right_to_work: "", transport: "", emergency_contact: "",
  });
  const [skillsText, setSkillsText] = useState("");
  const [locText, setLocText] = useState("");
  const [homeGeo, setHomeGeo] = useState(null);
  const [geocoding, setGeocoding] = useState(false);

  useEffect(() => {
    api.get("/staff/profile").then((r) => {
      const prof = r.data.profile || {};
      setP((prev) => ({ ...prev, ...prof }));
      setSkillsText((prof.skills || []).join(", "));
      setLocText((prof.preferred_locations || []).join(", "));
      if (prof.home_lat != null && prof.home_lon != null) {
        setHomeGeo({ lat: prof.home_lat, lon: prof.home_lon, at: prof.home_geocoded_at });
      }
    });
  }, []);

  const geocodeHome = async () => {
    setGeocoding(true);
    try {
      const r = await api.post("/worker/home-location", {});
      setHomeGeo({ lat: r.data.lat, lon: r.data.lon, at: new Date().toISOString() });
      toast.success("Home location geocoded — you'll get shifts even without live GPS.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not geocode postcode");
    } finally { setGeocoding(false); }
  };

  const save = async () => {
    try {
      await api.put("/staff/profile", {
        ...p,
        skills: skillsText.split(",").map(s => s.trim()).filter(Boolean),
        preferred_locations: locText.split(",").map(s => s.trim()).filter(Boolean),
      });
      toast.success("Profile saved");
      // Re-geocode if postcode was edited (already toasts on its own)
      if (p.postcode) { await geocodeHome(); }
    } catch { toast.error("Save failed"); }
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <div>
        <div className="label-caps">Your details</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Profile</h1>
      </div>
      <div className="card-tactile p-6 grid md:grid-cols-2 gap-4">
        <div><Label className="label-caps">First name</Label><Input value={user?.first_name || ""} disabled className="h-11 mt-1.5" /></div>
        <div><Label className="label-caps">Email</Label><Input value={user?.email || ""} disabled className="h-11 mt-1.5" /></div>
        <div><Label className="label-caps">Date of birth</Label><Input data-testid="prof-dob" type="date" value={p.date_of_birth || ""} onChange={(e)=>setP({...p, date_of_birth: e.target.value})} className="h-11 mt-1.5" /></div>
        <div><Label className="label-caps">City</Label><Input data-testid="prof-city" value={p.city || ""} onChange={(e)=>setP({...p, city: e.target.value})} className="h-11 mt-1.5" /></div>
        <div>
          <Label className="label-caps flex items-center gap-1"><MapPin size={12}/> Home postcode</Label>
          <Input data-testid="prof-postcode" value={p.postcode || ""} onChange={(e)=>setP({...p, postcode: e.target.value})} className="h-11 mt-1.5" />
          <div className="mt-1.5 flex items-center justify-between text-xs">
            <span className={homeGeo ? "text-emerald-700" : "text-amber-700"}>
              {homeGeo ? `Geocoded ✓ (${homeGeo.lat.toFixed(3)}, ${homeGeo.lon.toFixed(3)})` : "Not geocoded yet — click to enable live offers"}
            </span>
            <button type="button" onClick={geocodeHome} disabled={geocoding || !p.postcode} data-testid="prof-geocode-home" className="text-blue-600 font-semibold disabled:text-slate-400">
              {geocodeButtonLabel({ geocoding, homeGeo })}
            </button>
          </div>
        </div>
        <div><Label className="label-caps">Travel distance (miles)</Label><Input data-testid="prof-travel" type="number" value={p.travel_distance || 0} onChange={(e)=>setP({...p, travel_distance: Number(e.target.value)})} className="h-11 mt-1.5" /></div>
        <div className="md:col-span-2"><Label className="label-caps">Address</Label><Input data-testid="prof-address" value={p.address || ""} onChange={(e)=>setP({...p, address: e.target.value})} className="h-11 mt-1.5" /></div>
        <div className="md:col-span-2"><Label className="label-caps">Skills (comma separated)</Label><Input data-testid="prof-skills" value={skillsText} onChange={(e)=>setSkillsText(e.target.value)} placeholder="e.g. Warehousing, Forklift, First Aid" className="h-11 mt-1.5" /></div>
        <div className="md:col-span-2"><Label className="label-caps">Preferred locations</Label><Input data-testid="prof-locs" value={locText} onChange={(e)=>setLocText(e.target.value)} placeholder="Manchester, Leeds" className="h-11 mt-1.5" /></div>
        <div><Label className="label-caps">Right to work</Label><Input data-testid="prof-rtw" value={p.right_to_work || ""} onChange={(e)=>setP({...p, right_to_work: e.target.value})} className="h-11 mt-1.5" /></div>
        <div><Label className="label-caps">Transport</Label><Input data-testid="prof-transport" value={p.transport || ""} onChange={(e)=>setP({...p, transport: e.target.value})} className="h-11 mt-1.5" /></div>
        <div className="md:col-span-2"><Label className="label-caps">Experience</Label><Textarea data-testid="prof-experience" value={p.experience || ""} onChange={(e)=>setP({...p, experience: e.target.value})} rows={3} className="mt-1.5" /></div>
        <div className="md:col-span-2"><Label className="label-caps">Emergency contact</Label><Input data-testid="prof-emergency" value={p.emergency_contact || ""} onChange={(e)=>setP({...p, emergency_contact: e.target.value})} className="h-11 mt-1.5" /></div>
      </div>
      <Button data-testid="prof-save-btn" onClick={save} className="bg-blue-600 hover:bg-blue-700 rounded-md h-12 px-8">Save changes</Button>
    </div>
  );
}
